// The capture must be evaluated before every other module: it removes the MCP interaction secret
// from the address bar before Sentry, the router or any other import can read the URL.
// eslint-disable-next-line simple-import-sort/imports -- the sort would move the capture below the others
import { isMcpAuthDocument } from './app/mcp/bootstrap/mcp-interaction-capture'
import { provideZoneChangeDetection } from '@angular/core'
import { bootstrapApplication } from '@angular/platform-browser'

import * as Sentry from '@sentry/angular'
import { Amplify } from 'aws-amplify'

import { appConfig } from './app/app.config'
import { TIAdminAppComponent } from './app/common/components/app/ti-admin-app.component'
import {
  sanitizeMcpBreadcrumb,
  sanitizeMcpEvent,
} from './app/mcp/bootstrap/mcp-telemetry-sanitizer'
import env from './env'
import { APP_VERSION } from './env/version'

const isE2eEnv = 'Cypress' in window
const isLocalhost =
  window?.location?.hostname === 'localhost' || window?.location?.hostname === '127.0.0.1'

// The MCP authorization documents carry a one-use secret and the account the user is signing in
// with, so nothing is recorded or replayed for them.
Sentry.init({
  dsn: env.sentry.dsn,
  release: `${env.sentry.project}@${APP_VERSION}`,
  enabled: env.sentry.enabled && !isLocalhost && !isE2eEnv,
  ignoreErrors: ['UserUnAuthenticatedException'],
  integrations: [
    Sentry.browserTracingIntegration(),
    ...(isMcpAuthDocument
      ? []
      : [
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            networkDetailAllowUrls: env.sentry.networkDetailAllowUrls,
            // Application and Flespi Monitoring responses carry cross-tenant identifiers, Flespi device
            // ids and diagnostic text; their request and response bodies must never reach a replay,
            // whatever the allow list grows to include. This covers network capture only. On screen,
            // the Application result lists and drawers and the Flespi per-plugin results are wrapped
            // in `sentry-block`, in compact and desktop layouts alike; aggregate counts stay visible.
            // The account lookup is listed for the same reason: its response body is a list of
            // customer names keyed by tenant id. The MCP authorization calls carry session proofs and
            // one-use secrets.
            networkDetailDenyUrls: [
              /\/service\/application\//,
              /\/service\/flespi\//,
              /\/account\/lookup$/,
              /\/mcp\/oauth\//,
            ],
          }),
          Sentry.replayCanvasIntegration(),
        ]),
  ],
  beforeBreadcrumb: sanitizeMcpBreadcrumb,
  beforeSend: sanitizeMcpEvent,
  beforeSendTransaction: sanitizeMcpEvent,
  // Performance Monitoring
  tracesSampleRate: isMcpAuthDocument ? 0 : 0.5, //  Capture 50% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: env.sentry.tracePropagationTargets,
  // Session Replay
  replaysSessionSampleRate: isMcpAuthDocument ? 0 : 0.1,
  replaysOnErrorSampleRate: isMcpAuthDocument ? 0 : 1.0,
})

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: env.amplify.userPoolId,
      allowGuestAccess: false,
      userPoolClientId: env.amplify.userPoolClientId,
      identityPoolId: env.amplify.identityPoolId,
    },
  },
})

bootstrapApplication(TIAdminAppComponent, {
  ...appConfig,
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    ...appConfig.providers,
  ],
})
  .then(() => {
    const loadingElement = document.getElementById('loading-animation')
    if (loadingElement) {
      loadingElement.parentNode?.removeChild(loadingElement)
    }
  })
  .catch((e) => console.error(`Cannot bootstrap application`, e))
